import React from "react";
import { Grid, Box, Typography, Button, Pagination, LinearProgress } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Research } from "../types";
import ResearchCard from "./ResearchCard";

interface ResearchListProps {
  researches: Research[];
  loading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onViewResearch: (uuid: string) => void;
  onDeleteResearch: (uuid: string) => void;
  onDownloadResearch: (event: React.MouseEvent<HTMLElement>, uuid: string) => void;
  onSaveToTextFile: (uuid: string) => void;
  onCreateNew: () => void;
}

const ResearchList: React.FC<ResearchListProps> = ({
  researches,
  loading,
  page,
  totalPages,
  onPageChange,
  onViewResearch,
  onDeleteResearch,
  onDownloadResearch,
  onSaveToTextFile,
  onCreateNew,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* ローディング */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* 調査一覧 */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.5),
          backdropFilter: "blur(10px)",
          borderRadius: 2,
          p: 2,
          overflow: "hidden",
        }}
      >
        {/* スクロール可能なリスト部分 */}
        <Box sx={{ flex: 1, overflow: "auto", mb: 2 }}>
          <Grid container spacing={2}>
            {researches.map((research) => (
              <Grid size={{ xs: 12, sm: 6 }} key={research.uuid}>
                <ResearchCard
                  research={research}
                  onView={onViewResearch}
                  onDelete={onDeleteResearch}
                  onDownload={onDownloadResearch}
                  onSaveToTextFile={onSaveToTextFile}
                />
              </Grid>
            ))}
          </Grid>

          {researches.length === 0 && !loading && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t("deepResearch.emptyState.noResearches")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("deepResearch.emptyState.noResearchesDescription")}
              </Typography>
              <Button disableElevation variant="contained" startIcon={<AddIcon />} onClick={onCreateNew}>
                {t("deepResearch.emptyState.createNewResearch")}
              </Button>
            </Box>
          )}
        </Box>

        {/* 固定ページネーション */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", flexShrink: 0 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => onPageChange(newPage)}
              color="primary"
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default ResearchList;
